import { createServerFn } from "@tanstack/react-start";

import {
	authMiddleware,
	mondayBoards,
	userServices,
} from "@onesolution/middlewares";

import { BOARDS_KEY_MAPPER_ENUM, TASKS_COLUMNS } from "@onesolution/constants";
import { NOTIFICATIONS_COLUMNS } from "@onesolution/constants/monday/notifications";
import { mondayClient } from "@onesolution/monday/monday";
import type { OperationMessage } from "@onesolution/monday/queries/operationDetails.graphql";
import {
	OperationFiltersParams,
	makeOperationsFilters,
} from "@onesolution/utils/makeOperationFilters";
import { endOfDay, startOfDay } from "date-fns";
import {
	formatGlobalStatus,
	getStatusChoicesFromServer,
	wrapWithUseQuery,
} from "..";
const adminEmail = import.meta.env.VITE_PUBLIC_ADMIN_EMAIL;

export function filterMessages(messages: OperationMessage[]) {
	return messages.filter(
		(msg) =>
			msg.creator.email === adminEmail ||
			msg.text_body?.includes(`@${adminEmail}`),
	);
}

function getTimelineRange(datesString: string) {
	const dates = datesString.split(", ").flatMap((s) => s.split(" - "));
	const validDates = dates.filter(Boolean).map((d) => new Date(d!)) as Date[];

	if (validDates.length === 0) {
		return { startAt: null, endAt: null };
	}

	const startAt = startOfDay(
		new Date(Math.min(...validDates.map((d) => d.getTime()))),
	).toISOString();
	const endAt = endOfDay(
		new Date(Math.max(...validDates.map((d) => d.getTime()))),
	).toDateString();

	return { startAt, endAt };
}

const handler = createServerFn()
	.middleware([authMiddleware, mondayBoards, userServices])
	.validator(OperationFiltersParams)
	.handler(async ({ context, data }) => {
		const [{ items: operations, cursor }, notifs] = await Promise.all([
			mondayClient.query.operationsPage(context, {
				cursor: data.cursor,
				queryParams: await makeOperationsFilters(context, data),
			}),
			mondayClient.query.notifications(context, {
				onlyNotProcessed: true,
				columnIds: [NOTIFICATIONS_COLUMNS.pulseId],
			}),
		]);

		const choices = await getStatusChoicesFromServer({
			data: {
				boardId: BOARDS_KEY_MAPPER_ENUM.tasks,
				columnId: TASKS_COLUMNS.status,
			},
		});

		return {
			cursor,
			items: operations.map((op) => {
				const tags = [op.marketingPlans, op.marketingPlans, op.campaigns]
					.flatMap((br) => br.linked_items.map(({ name }) => name))
					.concat(op.entities.map(({ label }) => label));

				const timeline = getTimelineRange(op.timeline);

				return {
					id: op.id,
					name: op.name,
					parentId: "root",
					hasUnreadNotif: Boolean(notifs.find((n) => n.pulseId === op.id)),
					thumbnail: op.thumbnail?.[0],
					messagesCount: filterMessages(op.updates).length,
					...timeline,
					status: op.status,
					assignees: op.assignees.linked_items.map((user) => ({
						name: user.name,
						avatar: user.assets?.[0]?.public_url,
					})),
					boxFolderId: op.boxFolderId,
					deadline: op.deadline,
					services: op.services.linked_items,
					tags: tags,
					proof: { link: op.proof, status: op.proofStatus },
					quotes: op.quotes?.[0]
						? {
								link: op.quotes[0].quoteLink,
								status: op.quotes[0].status,
							}
						: null,
					progress: {
						status: { label: "", color: "" },
						global: formatGlobalStatus(op.productsStatus, choices),
					},
					subitemIds: op.products.linked_item_ids,
				};
			}),
		};
	});

export const getOperationsBaseFromServer = wrapWithUseQuery(
	"operations-base",
	handler,
);

export type OperationsList = Awaited<
	ReturnType<typeof getOperationsBaseFromServer>
>["items"];
